import {useState, useEffect, useContext, useCallback, useMemo, useRef} from "react";
import {useParams, useSearchParams} from "react-router-dom";
import styled from "styled-components";
import dayjs from "dayjs";

// Utils
import useMountedState from "../../hooks/useMountedState.js";
import useApi from "../../hooks/useApi.js";
import {useSocket} from "../../contexts/socket.js";
import {NavContext} from "../../contexts/nav.js";
import {AuthContext} from "../../contexts/auth.js";
import {SettingsContext} from "../../contexts/settings.js";
import {FacilityNavContext} from "../../contexts/facilitynav.js";
import {formatSubmissions} from "../../utils/builder.js";
import {getCompletionRate, prettyDateInUserTimezone} from "../../utils/helpers.js";
import {getWeatherSourceLinks} from "../../utils/weather-links.js";
import {
  DEFAULT_FACILITY_STATE_ID,
  EXTERNAL_OUTCOME,
  getExternalChecksheetOutcome,
  getLinkedChecksheetDisplay
} from "../../utils/externalChecksheetState.js";

// Components
import RenderChecksheet from "../checksheet-builder/RenderChecksheet.js";
import PrecipitationChanceIndicator from "../../components/PrecipitationChanceIndicator.js";

// Style
import {pad, border, radius} from "../../style/components/variables.js";
import {
  Abbr,
  Inline,
  Loader,
  NotLoaded,
  Page,
  Pill,
  Text,
  Title
} from "../../style/components/general.js";

// Socket Constants
import Room, {
  LIST_LOCKS,
  LOCK,
  RELEASE,
  ROOM_JOINED,
  getFacilityRooms,
  LISTEN_NOTIFY_FREQUENCY_CHANGE,
  LISTEN_NOTIFY_CHECKSHEETS_NEED_UPDATE
} from "../general/Room.js";

const LISTEN_NOTIFY_COMPLETED = "notify:completed";
const LISTEN_NOTIFY_DRAFT = "notify:draft";

const NOTIFY_COMPLETED = "notify_completed";
const NOTIFY_DRAFT = "notify_draft";

const Checksheet = () => {
  const {slug, checksheetSlug} = useParams();
  const [searchParams] = useSearchParams();
  const dateDueParam = searchParams.get("dateDue");
  const expectedFacilityStateId = searchParams.get("facilityStateId");
  const checksheetId = checksheetSlug.replace("checksheet_", "");
  const room = `${slug}-tasks-checksheet`;

  const isMounted = useMountedState();

  const socket = useSocket();

  const {facility, setFacility, available} = useContext(FacilityNavContext);

  const {allStates, getStates, showManageSessions, setShowManageSessions} = useContext(NavContext);
  const {settings} = useContext(SettingsContext);

  const {currentUser} = useContext(AuthContext);

  const {api: apiFacility} = useApi("facilities");
  const {api: apiChecksheets} = useApi("checksheets");
  const {api: apiChecksheetRecords} = useApi("checksheet-records");
  const {api: apiUsers} = useApi("users");

  const [locks, setLocks] = useState({});
  const [roomReady, setRoomReady] = useState(false);
  const [active, setActive] = useState(null);
  const [checksheet, setChecksheet] = useState(null);
  const [canAccess, setCanAccess] = useState(false);
  const [keyHolder, setKeyHolder] = useState(null);
  const [recordDraft, setRecordDraft] = useState(null);
  const [sharedResponses, setSharedResponses] = useState({});
  const [completeLoading, setCompleteLoading] = useState(null);
  const [draftLoading, setDraftLoading] = useState(null);
  const [prevSubmission, setPrevSubmission] = useState(null);
  const [justCompleted, setJustCompleted] = useState(false);
  // External checksheet links use the same manage-sessions modal as the rest of the app.
  const heldLockRef = useRef(false);
  const ignoreNextLockLossRef = useRef(false);
  // True after this tab emits LOCK — same-user locks from another session are not ours.
  const requestedLockRef = useRef(false);
  // Socket that took the lock — the server drops locks when that socket disconnects.
  const lockSocketIdRef = useRef(null);
  // Sync gate so we don't re-LOCK before showManageSessions state commits (would
  // notify the Tasks tab again and force a second Acknowledge).
  const suppressLockAcquireRef = useRef(false);
  const manageSessionsWasOpenRef = useRef(false);

  useEffect(() => {
    apiChecksheetRecords
      .callGet(null, {facilityChecksheetId: checksheetId, getPreviousSubmission: true})
      .then(({status, data}) => {
        if (status === 200 && data) setPrevSubmission(data);
      });
  }, [apiChecksheetRecords, checksheetId]);

  // Initial Load
  useEffect(() => {
    if (isMounted() && slug && facility === null)
      apiFacility.callGet(slug).then(({status, data}) => {
        if (status === 200) setFacility(data);
      });
  }, [isMounted, apiFacility, slug, facility, setFacility]);

  const getChecksheet = useCallback(
    () =>
      apiChecksheets
        .callGet(checksheetId, {
          withState: true,
          withCurrentState: true,
          allowOverdue: true,
          ...(dateDueParam ? {dateDue: dateDueParam} : {})
        })
        .then(({status, data}) => {
          if (status === 200) {
            const {restrictTo, recordDraft: recordDraftData} = data;
            const roleIds = restrictTo?.filter(({id}) => id)?.map(({id}) => id) || [];
            const userIds =
              restrictTo?.filter(({publicId}) => publicId)?.map(({publicId}) => publicId) || [];

            if (recordDraftData) {
              setRecordDraft(recordDraftData);
            } else {
              setRecordDraft(null);
            }

            setCanAccess(
              !!currentUser &&
                (!currentUser?.role?.id ||
                  userIds.includes(currentUser?.publicId) ||
                  roleIds.includes(currentUser.role.id))
            );
            setChecksheet(data);
          }
        }),
    [apiChecksheets, checksheetId, currentUser, dateDueParam]
  );

  useEffect(() => {
    if (isMounted() && checksheetId) {
      setJustCompleted(false);
      setChecksheet(null);
      setRecordDraft(null);
      getChecksheet();
    }
  }, [isMounted, checksheetId, getChecksheet, dateDueParam]);

  const facilityStates = useMemo(() => {
    if (!allStates || Object.keys(allStates).length === 0) return null;
    return [
      ...Object.entries(allStates).map(([name, {id, color}]) => ({
        name: name.toUpperCase(),
        id: `${id}`,
        color
      }))
    ];
  }, [allStates]);

  // Show the variant the email link points at (default vs state), not whichever
  // facility state is currently active — facilities can have multiple states on.
  // Completed records: use the record's state, ignore URL facilityStateId.
  const preferRecordState = Boolean(
    checksheet?.completedForDate ||
      (recordDraft && !recordDraft.draft && !recordDraft.overdue && recordDraft.completedAt)
  );

  const linkedDisplay = useMemo(
    () =>
      getLinkedChecksheetDisplay({
        expectedFacilityStateId,
        checksheet,
        record: recordDraft,
        preferRecordState
      }),
    [expectedFacilityStateId, checksheet, recordDraft, preferRecordState]
  );

  const linkOutcome = useMemo(
    () =>
      getExternalChecksheetOutcome({
        dateDueParam,
        expectedFacilityStateId,
        checksheet,
        recordDraft,
        facilityStates: facilityStates || [],
        timezone: settings?.timezone || "US/Pacific"
      }),
    [
      dateDueParam,
      expectedFacilityStateId,
      checksheet,
      recordDraft,
      facilityStates,
      settings?.timezone
    ]
  );

  const displayChecksheet = useMemo(() => {
    if (!checksheet || !linkedDisplay) return checksheet;
    return {
      ...checksheet,
      name: linkedDisplay.name,
      builder: linkedDisplay.builder,
      frequency: linkedDisplay.frequency,
      lastDay: linkedDisplay.lastDay,
      availableIndex: linkedDisplay.availableIndex,
      availableBase: linkedDisplay.availableBase
    };
  }, [checksheet, linkedDisplay]);

  useEffect(() => {
    const needsStates = Boolean(expectedFacilityStateId) || Boolean(linkedDisplay?.showStatePill);
    if (isMounted() && needsStates && !allStates) getStates();
  }, [isMounted, expectedFacilityStateId, linkedDisplay?.showStatePill, allStates, getStates]);

  const roomJoined = useCallback(({lockList}) => {
    setLocks(lockList ?? {});
    setRoomReady(true);
  }, []);

  const listLocks = useCallback(
    ({lockList, userId, fromPercentComplete, type}) => {
      if (
        (!type || type === "checksheet") &&
        (!userId ||
          (userId === currentUser.publicId && !fromPercentComplete) ||
          (userId !== currentUser.publicId && fromPercentComplete))
      )
        setLocks(lockList ?? {});
    },
    [currentUser]
  );

  const notifyDraft = useCallback(
    ({resourceKey}) => {
      if (resourceKey) {
        const [type, resourceId] = resourceKey.split("_");
        if (type === "checksheet" && resourceId === `${checksheetId}`) getChecksheet();
      }
    },
    [checksheetId, getChecksheet]
  );

  const notifyCompleted = useCallback(
    ({resourceKey}) => {
      if (resourceKey) {
        const [type, resourceId] = resourceKey.split("_");
        if (type === "checksheet" && resourceId === `${checksheetId}`) {
          setRecordDraft(null);
          getChecksheet();
        }
      }
    },
    [checksheetId, getChecksheet]
  );

  const notifyStateRefresh = useCallback(
    (payload = {}) => {
      const {checksheetIds} = payload;
      if (!checksheetIds || checksheetIds.map(id => `${id}`).includes(`${checksheetId}`))
        getChecksheet();
    },
    [checksheetId, getChecksheet]
  );

  useEffect(() => {
    socket.on(ROOM_JOINED, roomJoined);
    socket.on(LIST_LOCKS, listLocks);
    socket.on(LISTEN_NOTIFY_DRAFT, notifyDraft);
    socket.on(LISTEN_NOTIFY_COMPLETED, notifyCompleted);
    socket.on(LISTEN_NOTIFY_FREQUENCY_CHANGE, notifyStateRefresh);
    socket.on(LISTEN_NOTIFY_CHECKSHEETS_NEED_UPDATE, notifyStateRefresh);

    return () => {
      socket.off(ROOM_JOINED, roomJoined);
      socket.off(LIST_LOCKS, listLocks);
      socket.off(LISTEN_NOTIFY_DRAFT, notifyDraft);
      socket.off(LISTEN_NOTIFY_COMPLETED, notifyCompleted);
      socket.off(LISTEN_NOTIFY_FREQUENCY_CHANGE, notifyStateRefresh);
      socket.off(LISTEN_NOTIFY_CHECKSHEETS_NEED_UPDATE, notifyStateRefresh);
    };
  }, [socket, roomJoined, listLocks, notifyDraft, notifyCompleted, notifyStateRefresh]);

  useEffect(() => {
    if (!currentUser?.publicId || !checksheetSlug) return;

    const lock = locks?.[checksheetSlug];
    const weHoldLock = lock?.user === currentUser.publicId;

    if (weHoldLock && requestedLockRef.current) {
      heldLockRef.current = true;
      return;
    }

    // Lock lost after we held it — show manage-sessions unless another user holds it or we released on save.
    if (heldLockRef.current && !weHoldLock) {
      heldLockRef.current = false;
      requestedLockRef.current = false;
      if (ignoreNextLockLossRef.current) {
        ignoreNextLockLossRef.current = false;
        return;
      }
      const takenByOtherUser = lock && lock.user !== currentUser.publicId;
      // Phones drop the socket while the camera or file picker is open — the lock-acquire
      // effect takes it back, so there is no other session to manage.
      const droppedByReconnect = !lock && lockSocketIdRef.current !== socket.id;
      if (!takenByOtherUser && !droppedByReconnect) {
        // Block re-acquire synchronously — setState alone is too late for the
        // lock-acquire effect in this same commit and would re-notify other tabs.
        suppressLockAcquireRef.current = true;
        setShowManageSessions(true);
      }
    }
  }, [locks, checksheetSlug, currentUser, setShowManageSessions, socket]);

  useEffect(() => {
    // Only clear the sync gate after the modal has been shown and dismissed.
    // Clearing whenever showManageSessions is false undoes suppress set in the
    // lock-loss effect during the same commit (modal state not updated yet).
    if (showManageSessions) {
      suppressLockAcquireRef.current = true;
      manageSessionsWasOpenRef.current = true;
      return;
    }
    if (manageSessionsWasOpenRef.current) {
      // Acknowledge always RELEASE_LOCKS. If we still hold the lock, the empty
      // lock:list that follows is intentional — do not reopen manage-sessions.
      // (Nav's socket suppress does not cover this Checksheet lock-loss path.)
      if (heldLockRef.current) ignoreNextLockLossRef.current = true;
      suppressLockAcquireRef.current = false;
      manageSessionsWasOpenRef.current = false;
    }
  }, [showManageSessions]);

  useEffect(() => {
    // Acquire lock for completable form once manage-sessions is cleared.
    if (!isMounted() || !currentUser?.publicId || !checksheet) return;
    if (showManageSessions || suppressLockAcquireRef.current) return;
    if (linkOutcome?.type !== EXTERNAL_OUTCOME.FORM) return;

    const lock = locks[checksheetSlug];
    const heldByOtherUser = Boolean(lock && lock.user !== currentUser.publicId);
    if (heldByOtherUser) return;
    if (lock?.user === currentUser.publicId) {
      // Lock state already confirms we hold it (e.g. it was attributed to us
      // before this effect's first run) — mark it held now instead of waiting
      // on a redundant re-acquire round trip, so a loss in that window is caught.
      heldLockRef.current = true;
      requestedLockRef.current = true;
      if (!lockSocketIdRef.current) lockSocketIdRef.current = socket.id;
      return;
    }

    requestedLockRef.current = true;
    lockSocketIdRef.current = socket.id;
    socket.emit(LOCK, [...getFacilityRooms(slug, "checksheet"), room], checksheetSlug);
  }, [
    isMounted,
    currentUser,
    checksheet,
    slug,
    locks,
    checksheetSlug,
    room,
    socket,
    linkOutcome?.type,
    showManageSessions
  ]);

  useEffect(() => {
    if (isMounted() && currentUser?.publicId && !checksheet?.completed && locks) {
      if (locks[checksheetSlug] && locks[checksheetSlug].user !== currentUser.publicId) {
        apiUsers.callGet(locks[checksheetSlug].user).then(({status, data}) => {
          if (status === 200) {
            const {firstName, lastName} = data;
            setKeyHolder({
              name: `${firstName} ${lastName}`,
              percentage: locks[checksheetSlug]?.percentage
            });
          }
        });
      }

      if (!locks[checksheetSlug]) {
        setKeyHolder(null);
      }
    }
  }, [isMounted, currentUser, checksheet, slug, locks, checksheetSlug, room, apiUsers, socket]);

  const saveChecksheet = (
    record,
    draftId = null,
    isDraft = false,
    outOfRange = null,
    outOfRangeMultiple = null,
    notifyFieldCondition = null
  ) => {
    const params = {
      ...record,
      draft: isDraft,
      outOfRange: outOfRange ? Object.keys(outOfRange) : null,
      outOfRangeMultiple: outOfRangeMultiple ? Object.keys(outOfRangeMultiple) : null,
      notifyFieldCondition: notifyFieldCondition ? Object.keys(notifyFieldCondition) : null,
      withSharedResponses: true
    };

    // Only attach the state variant when the email link targets that state.
    // Default links complete against the base checksheet even if a variant exists.
    // Explicit null prevents API omit→active-state fallback from hijacking default links.
    const linkTargetsState =
      expectedFacilityStateId &&
      expectedFacilityStateId !== DEFAULT_FACILITY_STATE_ID &&
      checksheet?.state &&
      `${checksheet.state.facilityStateId}` === `${expectedFacilityStateId}`;

    if (linkTargetsState && checksheet.stateActive) params.stateId = checksheet.state.id;
    else if (
      expectedFacilityStateId === DEFAULT_FACILITY_STATE_ID ||
      (expectedFacilityStateId && !linkTargetsState)
    )
      params.stateId = null;

    const success = draftId ? 200 : 201;
    const request = draftId
      ? apiChecksheetRecords.callPut(draftId, params)
      : apiChecksheetRecords.callPost(params);

    request.then(({status, data: response}) => {
      if (status === success && response) {
        // Own draft/complete release should not be treated as a cross-tab session expiry.
        ignoreNextLockLossRef.current = true;
        socket.emit(
          RELEASE,
          [...getFacilityRooms(slug, "checksheet"), room],
          `checksheet_${checksheetId}`
        );

        if (isDraft) {
          if (response.data) setRecordDraft(response.data);
          socket.emit(NOTIFY_DRAFT, room, `checksheet_${checksheetId}`);
        } else {
          setJustCompleted(true);
          setRecordDraft(null);
          socket.emit(NOTIFY_COMPLETED, room, `checksheet_${checksheetId}`, true);
          setSharedResponses(response.sharedResponses || {});
        }
      }

      setCompleteLoading(null);
      setDraftLoading(null);
      getChecksheet();
    });
  };

  const buildChecksheetRecord = (
    responses,
    draftId = null,
    isDraft = false,
    outOfRange = null,
    outOfRangeMultiple = null,
    notifyFieldCondition = null
  ) => {
    const task = displayChecksheet || checksheet;
    const {id, name, frequency, builder} = task;

    const info = {
      userId: currentUser.publicId,
      facilityChecksheetId: id
    };

    if (isDraft) setDraftLoading(id);
    else setCompleteLoading(id);

    const {completedAt, ...rest} = responses;
    const formattedResponses = formatSubmissions(rest, builder);
    const submission = {
      name,
      responses: formattedResponses
    };

    if (frequency) submission.frequency = frequency.name;

    const checksheetRecord = {...info, submission: {...submission}, completedAt};

    const linkedDateDue = checksheet.targetDateDue ?? dateDueParam;
    if (linkedDateDue) checksheetRecord.dateDue = linkedDateDue;

    saveChecksheet(
      checksheetRecord,
      draftId,
      isDraft,
      outOfRange,
      outOfRangeMultiple,
      notifyFieldCondition
    );
  };

  const getCompletionRateHelper = () => {
    if (!recordDraft.submission.responses) return 0;
    const {completedAt: _ca, ...rest} = {...recordDraft.submission.responses};
    return getCompletionRate(rest);
  };

  const nextAvailable = useMemo(() => {
    if (checksheet?.dateDue && checksheet?.frequency) {
      const {
        dateDue,
        frequency: {interval, base}
      } = checksheet;
      const date =
        base === "days" && interval === 1
          ? "tomorrow"
          : `on ${prettyDateInUserTimezone(
              dayjs(dateDue).subtract(interval, base.replace("s", "")),
              settings.timezone,
              "MMM DD, YYYY"
            )}`;
      return date;
    }
    return "later";
  }, [checksheet, settings]);

  const facilityState = useMemo(() => {
    if (!linkedDisplay?.showStatePill || !facilityStates) return null;
    return facilityStates.find(({id}) => id === `${linkedDisplay.facilityStateId}`) || null;
  }, [linkedDisplay, facilityStates]);

  const linkedDateDue = linkOutcome?.linkedDateDue ?? checksheet?.targetDateDue ?? dateDueParam;
  const outcomeType = linkOutcome?.type;
  const needsLockSession = outcomeType === EXTERNAL_OUTCOME.FORM;
  const showChecksheetForm = canAccess && !keyHolder && needsLockSession && roomReady;

  const formatLinkedDay = due =>
    due ? prettyDateInUserTimezone(due, settings.timezone, "MMM DD, YYYY") : null;

  let checksheetBody = null;
  if (outcomeType === EXTERNAL_OUTCOME.STATE_MISMATCH) {
    checksheetBody = (
      <Unavailable>
        <Text>{linkOutcome.message}</Text>
      </Unavailable>
    );
  } else if (outcomeType === EXTERNAL_OUTCOME.NOT_DUE) {
    let notDueMessage = "No record is required for this day.";
    if (linkOutcome.availableUntil) {
      notDueMessage = `No record is required until ${formatLinkedDay(linkOutcome.availableUntil)}.`;
    } else if (linkedDateDue) {
      notDueMessage = `No record is required for ${formatLinkedDay(linkedDateDue)}.`;
    }
    checksheetBody = (
      <Unavailable>
        <Text>{notDueMessage}</Text>
      </Unavailable>
    );
  } else if (outcomeType === EXTERNAL_OUTCOME.COMPLETED) {
    let completedMessage;
    if (linkedDateDue && justCompleted) {
      completedMessage = `Checksheet completed for ${formatLinkedDay(linkedDateDue)}.`;
    } else if (linkedDateDue) {
      completedMessage = `Checksheet already completed for ${formatLinkedDay(linkedDateDue)}.`;
    } else if (justCompleted) {
      completedMessage = `Checksheet completed. Check back ${nextAvailable}.`;
    } else {
      completedMessage = `Checksheet already completed for this availablity window. Check back ${nextAvailable}.`;
    }
    checksheetBody = (
      <Unavailable>
        <Text>{completedMessage}</Text>
      </Unavailable>
    );
  } else if (locks && displayChecksheet?.builder?.allIds?.length > 0) {
    if (showChecksheetForm) {
      checksheetBody = (
        <RenderChecksheet
          room={room}
          task={displayChecksheet}
          responses={recordDraft ? recordDraft.submission.responses : null}
          previousSubmission={prevSubmission}
          sharedResponses={sharedResponses}
          setSubmission={(responses, outOfRange, outOfRangeMultiple, notifyFieldCondition) =>
            buildChecksheetRecord(
              responses,
              recordDraft ? recordDraft.id : null,
              false,
              outOfRange,
              outOfRangeMultiple,
              notifyFieldCondition
            )
          }
          setDraft={(responses, outOfRange, outOfRangeMultiple, notifyFieldCondition) =>
            buildChecksheetRecord(
              responses,
              recordDraft ? recordDraft.id : null,
              true,
              outOfRange,
              outOfRangeMultiple,
              notifyFieldCondition
            )
          }
          completeLoading={completeLoading === displayChecksheet.id}
          draftLoading={draftLoading === displayChecksheet.id}
          dateDue={recordDraft ? recordDraft.dateDue : linkedDateDue ?? displayChecksheet.dateDue}
          startDate={
            displayChecksheet.startDate ??
            linkedDateDue ??
            (recordDraft ? recordDraft.dateDue : null)
          }
          showDates
          user={recordDraft ? `${recordDraft.user.firstName} ${recordDraft.user.lastName}` : null}
          completedAt={recordDraft ? recordDraft.completedAt : null}
          draftDate={recordDraft ? recordDraft.createdAt : null}
          readOnly={
            !locks || (locks[checksheetSlug] && locks[checksheetSlug].user !== currentUser.publicId)
          }
          preview={false}
          persistToLocalStorage
          allowDeleteFromLocalStorage
          freezeComponentState={false}
        />
      );
    } else if (outcomeType === EXTERNAL_OUTCOME.FORM && !roomReady) {
      checksheetBody = (
        <NotLoaded>
          <Loader />
        </NotLoaded>
      );
    } else {
      checksheetBody = (
        <Unavailable>
          <Text>
            {keyHolder &&
              `Locked by ${keyHolder.name} (Progress: ${
                keyHolder?.percentage ? keyHolder.percentage : 0
              }%).`}
            {!keyHolder &&
              !canAccess &&
              "You do not have permission to access this checksheet. If this is a mistake please contact you system administrator."}
            {!keyHolder &&
              canAccess &&
              `Checksheet already completed for this availablity window. Check back ${nextAvailable}.`}
          </Text>
        </Unavailable>
      );
    }
  }

  return (
    <ChecksheetPage hasMenu={available.length > 1}>
      <Room
        name="tasks-checksheet"
        active={active}
        setActive={setActive}
        withLocks={needsLockSession}
        shouldJoinRoom={needsLockSession}
      />

      <ChecksheetWrapper>
        <Inline>
          {displayChecksheet?.name && (
            <ChecksheetName>
              <Abbr title={displayChecksheet.name}>{displayChecksheet.name.toUpperCase()}</Abbr>
            </ChecksheetName>
          )}
          {facility?.isDeleted && (
            <Archived data-testid="facility.archived">
              <Abbr title="Archived">ARCHIVED</Abbr>
            </Archived>
          )}
          {facilityState && (
            <Pill color={facilityState?.color ? `#${facilityState.color}` : undefined}>
              <Abbr title={facilityState?.name || "DEFAULT"}>
                {facilityState?.name || "DEFAULT"}
              </Abbr>
            </Pill>
          )}
          {displayChecksheet?.frequency?.name && (
            <Frequency>
              <Abbr title={displayChecksheet.frequency.name}>
                {displayChecksheet.frequency.name}
              </Abbr>
            </Frequency>
          )}
          {recordDraft?.draft && recordDraft.submission && (
            <Draft>
              <Abbr title="INCOMPLETE">
                INCOMPLETE&nbsp;
                {getCompletionRateHelper(recordDraft.submission.responses)}%
              </Abbr>
            </Draft>
          )}
          {recordDraft && recordDraft.overdue && (
            <Overdue>
              <Abbr title="OVERDUE">OVERDUE</Abbr>
            </Overdue>
          )}
        </Inline>
        {!checksheet && (
          <NotLoaded>
            <Loader />
          </NotLoaded>
        )}
        {locks && displayChecksheet?.builder?.allIds?.length > 0 && showChecksheetForm && (
          <PrecipitationChanceIndicator
            precipitationChance={checksheet.precipitationChance}
            windDirection={checksheet.windDirection}
            windSpeed={checksheet.windSpeed}
            links={getWeatherSourceLinks(facility?.primaryAddress || {})}
          />
        )}
        {checksheetBody}
      </ChecksheetWrapper>
    </ChecksheetPage>
  );
};

// Style Overrides
const ChecksheetPage = styled(Page)`
  padding-bottom: ${pad * 2}px;
`;

const ChecksheetWrapper = styled.div`
  margin: ${pad * 2}px 0;
  width: 100%;
  padding: ${pad}px;
  border-radius: ${radius};
  border: ${border} solid ${({theme}) => theme.primary};
`;

const ChecksheetName = styled(Title)`
  align-self: start;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  max-width: 90%;
`;

const Draft = styled(Pill)`
  background: ${({theme}) => theme.warning};
`;

const Archived = styled(Pill)`
  background: ${({theme}) => theme.warning};
`;

const Overdue = styled(Pill)`
  background: ${({theme}) => theme.error};
`;

const Frequency = styled(Pill)`
  background: ${({theme}) => theme.primary};
`;

const Unavailable = styled.div`
  padding: ${pad}px 0;
`;

export default Checksheet;
